<script setup lang="ts">
import type { HTMLStyleElementWrapper } from '@@/bits/css'
import { appendCssToDocumentHead } from '@@/bits/css'
import { dir } from '@@/bits/current_dir'
import { $ } from '@@/bits/jquery'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Cid, Id, Post } from '@@/types'
import BaseIcon from '@@/vuecomponents/base_icon.vue'
import SurfaceContainerTimelinePostsLtr from '@@/vuecomponents/SurfaceContainerTimelinePostsLtr.vue'
import SurfaceContainerTimelinePostsRtl from '@@/vuecomponents/SurfaceContainerTimelinePostsRtl.vue'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

useDoubleClickToPost()

const { xHeader, headerHeight, backgroundLuminance, isReadOnly, isScreenshotMode, isDesktopLayout, isMobileLayout } =
  storeToRefs(useSurfaceStore())
const { canIWrite } = storeToRefs(useSurfacePermissionsStore())
const { commentsPanelPostId } = storeToRefs(useCommentsStore())
const { isAPostDragging } = storeToRefs(useSurfacePostsDragAndDropStore())
const surfacePostsStore = useSurfacePostsStore()
const { postBeingEditedCid, currentSortedPosts, newestPostCid } = storeToRefs(surfacePostsStore)
const { startNewPost } = surfacePostsStore

const canShowAddPostButton = computed<boolean>(() => {
  return !isDragging.value && !isReadOnly.value && canIWrite.value
})

const wishList = ref<HTMLElement>()

// #region Dragging post
const isDragging = ref(false)

const handleDraggingChanged = (dragging: boolean): void => {
  isDragging.value = dragging
  isAPostDragging.value = dragging
}
// #endregion

// #region Start/end marks
const startOfLine = ref<HTMLElement>()
const endOfLine = ref<HTMLElement>()
const isAtStart = ref(true)
const isAtEnd = ref(false)
let startIntersectionObserver: IntersectionObserver | null = null
let endIntersectionObserver: IntersectionObserver | null = null

const observeIntersection = (): void => {
  if (startOfLine.value != null) {
    startIntersectionObserver = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio === 0) {
        isAtStart.value = false
      } else {
        isAtStart.value = true
      }
    })
    startIntersectionObserver.observe(startOfLine.value)
  }

  if (endOfLine.value != null) {
    endIntersectionObserver = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio === 0) {
        isAtEnd.value = false
      } else {
        isAtEnd.value = true
      }
    })
    endIntersectionObserver.observe(endOfLine.value)
  }
}

const addBefore = (postAfter: Post): void => {
  // Timeline sort index is from smallest on the left to largest to the right
  let nextId: Id | null | undefined
  let previousId: Id | null | undefined
  // Determine index of post
  const postAfterIndex = currentSortedPosts.value.findIndex((p) => p.id === postAfter.id)
  if (postAfterIndex === 0) {
    // Post is added before first post, earliest in timeline, last in ordering
    nextId = currentSortedPosts.value[0].id
    previousId = null
  } else {
    // Sandwiched in between
    nextId = currentSortedPosts.value[postAfterIndex - 1].id
    previousId = currentSortedPosts.value[postAfterIndex].id
  }
  const { sortIndex } = computePostIndexFromPost(null, {
    prevId: `wish-${nextId}`,
    nextId: `wish-${previousId}`,
  })
  startNewPost({
    attributes: {
      sortIndex: sortIndex ?? undefined,
    },
  })
}

const addToLast = (): void => {
  const { sortIndex } = computePostIndexFromPost(null, {
    prevId: null,
    nextId: null,
  })
  startNewPost({
    attributes: {
      sortIndex: sortIndex ?? undefined,
    },
  })
}

// #endregion

// #region Scroll to post
const SCROLL_PADDING = 74 // in px

const scrollToPostWithCid = (cid: Cid): void => {
  nextTick(() => {
    if (wishList.value == null) return
    const newWishElement = document.querySelector(`.surface-post[data-post-cid="${cid}"]`) as HTMLElement | null
    if (newWishElement == null) return
    const newWishWrapperElement = newWishElement.closest('.wish-wrapper') as HTMLElement | null
    if (newWishWrapperElement == null) return
    const rightEdgeOfNewWishInPx = newWishElement.getBoundingClientRect().right
    if (rightEdgeOfNewWishInPx + SCROLL_PADDING > document.body.offsetWidth) {
      const scrollLeft =
        newWishWrapperElement.offsetLeft + newWishElement.offsetWidth - document.body.offsetWidth + SCROLL_PADDING
      $(wishList.value).animate({ scrollLeft }, '250')
    }
  })
}

watch(postBeingEditedCid, (cid) => {
  if (cid != null) {
    scrollToPostWithCid(cid)
  }
})

watch(newestPostCid, (cid) => {
  if (cid != null) {
    scrollToPostWithCid(cid)
  }
})

watch(commentsPanelPostId, (postId) => {
  const lineExtendedElement = document.getElementById('line-extended')
  if (wishList.value == null || lineExtendedElement == null) return
  if (postId == null) {
    lineExtendedElement.style.width = '100%'
    return
  }
  const postElement = document.getElementById(`wish-${postId}`)
  if (postElement == null) return
  const wrapperElement = postElement.closest('.wish-wrapper') as HTMLElement | null
  if (wrapperElement == null) return
  // Scroll post into view when comment panel is opened
  const scrollLeft = wrapperElement.offsetLeft - SCROLL_PADDING
  lineExtendedElement.style.width = '100vw'
  $(wishList.value).animate({ scrollLeft }, '350')
})

// #endregion

// #region Scroll to start/end
const scrollToStart = (): void => {
  if (wishList.value == null) return
  $(wishList.value).animate({ scrollLeft: 0 }, '350')
}

const scrollToEnd = (): void => {
  if (wishList.value == null) return
  let scrollLeft = wishList.value.scrollWidth
  if (dir() === 'rtl') scrollLeft *= -1
  $(wishList.value).animate({ scrollLeft }, '350')
}
// #endregion

// #region Wish max height
let maxHeightForRealWishStyle: HTMLStyleElementWrapper | null = null

const updateWishMaxHeight = (headerHeight: number): void => {
  const bottomBarHeight = isMobileLayout.value ? 52 : 0
  const circleHeightPlusPaddingHeight = 24
  const approvalMessageHeight = 40
  // when a wish requires approval (.unapproved-wish), we need an extra 40px space for the approval UI
  const css = `
      @media (min-height: 828px) {
        .real-wish, .timeline-wish {
          max-height: calc(var(--vh100)/2 - ${headerHeight}px/2 - var(--distance-from-post-to-line) - ${circleHeightPlusPaddingHeight}px - ${bottomBarHeight}px/2 );
        }
        .real-wish, .timeline-wish.unapproved-wish {
          max-height: calc(var(--vh100)/2 - ${headerHeight}px/2 - var(--distance-from-post-to-line) - ${circleHeightPlusPaddingHeight}px - ${approvalMessageHeight}px - ${bottomBarHeight}px/2 );
        }
      }
      @media (max-height: 828px) {
        .real-wish, .timeline-wish {
          max-height: calc(var(--vh100) - ${headerHeight}px - var(--distance-from-post-to-line) - var(--small-screen-bottom-padding) - ${circleHeightPlusPaddingHeight}px - ${bottomBarHeight}px);
        }
        .real-wish, .timeline-wish.unapproved-wish {
          max-height: calc(var(--vh100) - ${headerHeight}px - var(--distance-from-post-to-line) - var(--small-screen-bottom-padding) - ${circleHeightPlusPaddingHeight}px - ${approvalMessageHeight}px - ${bottomBarHeight}px);
        }
      }
      `
  if (maxHeightForRealWishStyle != null) {
    maxHeightForRealWishStyle.update(css)
  } else {
    maxHeightForRealWishStyle = appendCssToDocumentHead(css)
  }
}

watch(headerHeight, (newHeaderHeight) => {
  updateWishMaxHeight(newHeaderHeight)
})
// #endregion

// #region Life cycle
onMounted(() => {
  nextTick(() => {
    updateWishMaxHeight(headerHeight.value)
    observeIntersection()
  })
})

onBeforeUnmount(() => {
  maxHeightForRealWishStyle?.remove()
  maxHeightForRealWishStyle = null

  startIntersectionObserver?.disconnect()
  startIntersectionObserver = null

  endIntersectionObserver?.disconnect()
  endIntersectionObserver = null
})
// #endregion
</script>

<template>
  <div id="wall-container" :class="isScreenshotMode && 'w-full h-full'">
    <SurfaceHeader v-if="xHeader" />
    <div
      id="wish-list"
      ref="wishList"
      :dir="dir()"
      :class="[
        'wish-list-wrapper',
        `background-luminance-${backgroundLuminance}`,
        {
          'scrollbar-photo': backgroundLuminance === 'light',
          'scrollbar-photo-dark': backgroundLuminance === 'dark',
        },
      ]"
    >
      <div class="wish-list-content">
        <SurfaceContainerTimelinePostsRtl
          v-if="dir() === 'rtl'"
          :post-being-edited-cid="postBeingEditedCid"
          :current-sorted-posts="currentSortedPosts"
          :reaction-ids-by-post-id="reactionIdsByPostId"
          :comment-ids-by-post-id="commentIdsByPostId"
          @dragging="handleDraggingChanged"
        />
        <SurfaceContainerTimelinePostsLtr
          v-else
          :post-being-edited-cid="postBeingEditedCid"
          :current-sorted-posts="currentSortedPosts"
          :reaction-ids-by-post-id="reactionIdsByPostId"
          :comment-ids-by-post-id="commentIdsByPostId"
          @dragging="handleDraggingChanged"
        />
        <div class="line-group-wrapper">
          <span ref="startOfLine" />
          <div v-for="post in currentSortedPosts" :key="post.cid" class="line-wrapper">
            <div :class="`line ${canShowAddPostButton ? '' : 'no-clipping'}`" />
            <button
              v-if="canShowAddPostButton"
              :class="['icon-button add-button', isDesktopLayout && 'v3']"
              @click="addBefore(post)"
            >
              <BaseIcon name="rounded_add" />
            </button>
          </div>

          <div class="line-wrapper">
            <div
              :class="`line background-luminance-${backgroundLuminance} ${canShowAddPostButton ? '' : 'no-clipping'}`"
            />
            <button
              v-if="canShowAddPostButton"
              :class="['icon-button add-button', isDesktopLayout && 'v3']"
              @click="addToLast"
            >
              <BaseIcon name="rounded_add" />
            </button>
          </div>
          <div class="line-wrapper line-wrapper-extender">
            <div id="line-extended" class="line extended" />
          </div>
          <button
            v-if="!isMobileLayout"
            :class="`icon-button scroll-button start ${
              isAtStart ? 'disabled' : ''
            } background-luminance-${backgroundLuminance}`"
            @click="scrollToStart"
          >
            <BaseIcon name="first_page" />
          </button>
          <button
            v-if="!isMobileLayout"
            :class="['icon-button scroll-button', 'end', isAtEnd && 'disabled', isDesktopLayout && 'v3']"
            @click="scrollToEnd"
          >
            <BaseIcon name="last_page" />
          </button>
          <span ref="endOfLine" />
        </div>
      </div>
      <!-- N.B. The clip path to remove the center dash where we put the plus button in place -->
      <svg width="0" height="0" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <clipPath id="clipping" clipPathUnits="objectBoundingBox">
            <!-- 0.428 = 3/7; 0.142 = 1/7
            Change these to the appropriate fraction number according to
            the number of dashes between circle n (n is odd): ((n-1)/2)/n and 1/n
            Draw from -0.5 to 1.5 to prevent clipping vertically
          -->
            <path d="M0 -0.5 h1 v2 h-1z M0.428 0 v2 h0.142 v-2z" />
          </clipPath>
        </defs>
      </svg>
    </div>
  </div>
</template>

<style lang="scss">
@import '@assets/stylesheets/3/modules/all';
@import '@@/styles/timeline.scss';
@include timeline-background-luminance-variables;

.real-wish,
.timeline-wish {
  --distance-from-post-to-line: #{$distance-from-post-to-line};
  --small-screen-bottom-padding: #{$small-screen-bottom-padding};
  --post-circle-total-diameter: #{$post-circle-total-diameter};
}

@mixin timeline-sizing($post-width, $distance-between-posts) {
  $line-dash-length: $distance-between-posts/$number-of-dashes-between-circles;
  // LINE
  .line-group-wrapper {
    .line-wrapper {
      min-width: $distance-between-posts;
      .line {
        min-width: $distance-between-posts;
        // dashed line
        background-position: $line-dash-length/4 0px;
        background-size: $line-dash-length $line-thickness;
      }
    }
  }

  // WISH LAYOUTING
  .wish-draggable-wrapper {
    padding-inline-start: $distance-between-posts / 2;
  }

  .wish-wrapper {
    min-width: $distance-between-posts;
    .wish-popup {
      width: $post-width;
      left: -($post-width - $distance-between-posts - $post-circle-total-diameter)/2;
      [dir='rtl'] & {
        left: auto;
        right: -($post-width - $distance-between-posts - $post-circle-total-diameter)/2;
      }
    }
  }
}

#wall-container {
  overflow: hidden;
  // WISH - BEING DRAGGED
  .wish .wish-frame {
    @include shadow-z2;
    border-radius: 12px;
    .real-wish {
      border-radius: 12px !important;
      overflow: auto;
      .surface-post-attachment {
        // N.B: this is so that the attachment will not have a border radius
        // and the post border radius will cover the .surface-post-attachment
        // correctly when the post is scrollable
        border-radius: 0px !important;
      }
    }
  }
  .wish-wrapper.sortable-chosen {
    .wish .wish-frame {
      @include shadow-z3;
    }
    .wish-popup-marker {
      @include shadow-z2;
    }
  }
}

#wish-list {
  position: relative;
}

// attachment preview
.with-attachment:not(.with-author) {
  .surface-beethoven-preview {
    img {
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
    }
  }
  .attachment-controls.remove-attachment {
    left: 0;
    right: unset !important;
  }
}

// RESPONSIVE
.wish-list-content {
  display: flex;
  flex-direction: row;
  padding-bottom: 0px;
  flex-grow: 1;
  align-items: center;
  overflow: visible;
}
.wish-list-wrapper#wish-list {
  display: flex;
  flex-direction: row;
  padding-bottom: 0px;
  overflow: auto;
  // flex-wrap: wrap;

  // LINE
  .line-group-wrapper {
    display: flex;
    flex-grow: 1;
    z-index: $line-zIndex;
    .line-wrapper {
      position: relative;
      display: inline-block;
      .line {
        position: absolute;
        display: inline-block;
        height: $line-thickness;
        top: calc(50% - #{$line-thickness/2});
        // dashed line
        // background-image: linear-gradient(90deg, rgba(#fff,0.8) 50%, transparent 0%);
        background-image: var(--timeline-dash-background-image);
        background-repeat: repeat-x;
        clip-path: url('#clipping');
        &.no-clipping {
          clip-path: none;
        }
      }
      .add-button {
        position: absolute;
        left: calc(50% - #{$add-button-size/2});
        [dir='rtl'] & {
          left: 0;
          right: calc(50% - #{$add-button-size/2});
        }
        top: calc(50% - #{$add-button-size/2 + 0.5px});
        i {
          font-size: $add-button-font-size;
          font-weight: 600;
          color: var(--add-button-icon-color);
        }

        &.v3 {
          [dir='rtl'] & {
            left: calc(50% - #{$add-button-size/2});
            right: calc(50% - #{$add-button-size/2});
          }
        }
      }
    }

    .line-wrapper-extender {
      flex-grow: 1;
      margin-left: 0px !important;
      min-width: 0px;
      .line.extended {
        width: 100%;
        min-width: 0px;
        clip-path: none;
      }
    }
    .icon-button.scroll-button {
      position: fixed;
      color: var(--scroll-button-text-color);
      background: var(--scroll-button-background);
      &:hover {
        color: var(--scroll-button-text-color-hover);
      }
      border-radius: 50%;
      backdrop-filter: blur(3px);
      transform: translateY(-50%);
      &.disabled {
        // hide the icon with scale 0;
        transform: translateY(-50%) scale(0);
      }
      &.start {
        left: 6px;
      }
      &.end {
        right: 6px;
        &.v3 {
          right: 78px;
        }
      }
      @include respondToPhone {
        border-radius: 12px;
        bottom: 0px;
        &.start {
          left: 16px;
        }
        &.end {
          left: 64px;
          right: unset;
        }
        &.disabled {
          pointer-events: none;
          transform: translateY(-50%);
          opacity: 0.7;
          color: var(--scroll-button-text-color-disabled) !important;
        }
      }
    }
  }

  // WISH LAYOUTING
  .wish-draggable-wrapper {
    display: flex;
    position: absolute;
    height: 100%;
  }

  // WISH POPUP
  .wish-wrapper {
    position: relative;
    height: 100%; // drop box
    .wish-popup {
      position: absolute;
      top: 50%;
      z-index: $dragged-zIndex;
    }
    .wish-popup-marker {
      .circle-marker {
        cursor: grab;
        position: absolute;
        top: calc(50% - #{$post-circle-total-diameter/2});
        width: $post-circle-inner-diameter;
        height: $post-circle-inner-diameter;
        background: transparent;
        border: $post-circle-border-width solid var(--circle-marker-border-color);
        border-radius: 50%;
        left: 50%;

        z-index: $dragged-zIndex;
      }

      .drag-vertical-line {
        display: none;
        &:before,
        &:after {
          content: '';
          height: calc(50% - #{$post-circle-total-diameter/2});
          width: $line-thickness;
          background: $highlight-color;
          left: calc(50% + #{$post-circle-total-diameter/2 - $line-thickness/2});

          position: absolute;
        }
        &:before {
          top: 0;
        }
        &:after {
          bottom: 0;
        }
      }
    }
    &.no-drag .wish-popup-marker {
      .circle-marker {
        cursor: default;
      }
    }
  }

  .wish-wrapper:not(.drag-just-end) .wish-popup {
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important; // Material ease https://material.io/design/motion/speed.html#easing
    .popup-bubble-anchor-tip {
      z-index: -1;
      // Material ease https://material.io/design/motion/speed.html#easing
      transition: top 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }
  }

  @mixin wish-above-line {
    .wish-popup {
      transform: translateY(calc(-100% - #{$distance-from-post-to-line}));
      .popup-bubble-anchor-tip {
        top: 100%;
        transform: translateX(-50%) scaleY(1);
      }
    }
  }
  @mixin wish-below-line {
    align-self: flex-end;
    .wish-popup {
      transform: translateY($distance-from-post-to-line + $line-thickness);
      .popup-bubble-anchor-tip {
        top: -16px;
        transform: translateX(-50%) scaleY(-1);
        clipPath {
          .chrome & {
            // chrome transform does not apply to svg, we have to transform them separately
            transform: translate(0px, 28px) scaleY(-1);
          }
        }
      }
    }
  }

  :not(.current-wishes) > .wish-wrapper.above-line {
    @include wish-above-line();
  }
  :not(.current-wishes) > .wish-wrapper.below-line {
    @include wish-below-line();
  }
  .current-wishes > .wish-wrapper:nth-of-type(odd) {
    @include wish-above-line();
  }
  .current-wishes > .wish-wrapper:nth-of-type(even) {
    @include wish-below-line();
  }
  .draggable-mirror .wish-popup {
    display: none;
  }
  .wish-wrapper.draggable-mirror {
    z-index: $dragged-zIndex !important;
  }
  .wish-wrapper {
    // z-index: $post-zIndex;
    .wish-popup-marker {
      z-index: $post-zIndex;
    }
    .wish-popup {
      z-index: $post-zIndex;
    }
  }
  .draggable-mirror .wish-popup-marker {
    .circle-marker {
      border: $post-circle-border-width solid $highlight-color;
    }
    .drag-vertical-line {
      display: block;
    }
  }

  .wish-wrapper:not(:last-of-type),
  .line-wrapper:not(:nth-last-of-type(2)):not(:last-of-type) {
    margin-inline-end: $post-circle-total-diameter;
  }
}

.wish-list-wrapper#wish-list[dir='rtl'] {
  .wish-wrapper .wish-popup-marker {
    .circle-marker {
      left: auto;
      right: 50%;
    }
    .drag-vertical-line {
      &:before,
      &:after {
        left: auto;
        right: calc(50% + #{$post-circle-total-diameter/2 - $line-thickness/2});
      }
    }
  }
  .line-group-wrapper .icon-button.scroll-button {
    &.start {
      left: auto;
      right: 6px;
      transform: translateY(-50%) scaleX(-1);
      &.disabled {
        transform: translateY(-50%) scale(0);
      }
    }
    &.end {
      left: 6px;
      &.v3 {
        left: 78px;
      }
      right: auto;
      transform: translateY(-50%) scaleX(-1);
      &.disabled {
        transform: translateY(-50%) scale(0);
      }
    }
  }
}

@include timeline-sizing($post-width-regular-screen, $distance-between-posts-regular-screen);
@media (max-height: 828px) {
  @include timeline-sizing($post-width-small-screen, $distance-between-posts-small-screen);
  // MOBILE LAYOUT
  .wish-list-wrapper#wish-list {
    .wish-list-content {
      align-items: flex-end;
      margin-bottom: $small-screen-bottom-padding;
      overflow-x: visible;
    }
    .wish-draggable-wrapper {
      padding-inline-start: $distance-between-posts-small-screen/2 - 90px;
    }
    .wish-list-content > .wish-wrapper:nth-of-type(odd),
    .wish-list-content > .wish-wrapper:nth-of-type(even),
    .wish-list-content > .wish-wrapper.above-line,
    .wish-list-content > .wish-wrapper.below-line,
    .wish-draggable-wrapper > .wish-wrapper:nth-of-type(odd),
    .wish-draggable-wrapper > .wish-wrapper:nth-of-type(even),
    .wish-draggable-wrapper > .current-wishes > .wish-wrapper:nth-of-type(odd),
    .wish-draggable-wrapper > .current-wishes > .wish-wrapper:nth-of-type(even),
    body > .wish-wrapper:nth-of-type(odd),
    body > .wish-wrapper:nth-of-type(even) {
      .wish-popup-marker {
        .circle-marker {
          top: unset;
          bottom: -$post-circle-total-diameter/2;
        }
        .drag-vertical-line {
          &:before {
            top: unset;
            bottom: $post-circle-total-diameter/2;
            height: calc(100% - #{$small-screen-bottom-padding + $post-circle-total-diameter/2});
          }
          &:after {
            top: calc(100% + #{$post-circle-total-diameter/2});
            height: $small-screen-bottom-padding - $post-circle-total-diameter/2;
          }
        }
      }
      .wish-popup {
        top: unset;
        bottom: 0;
        transform: translateY(-$distance-from-post-to-line);
      }
      .wish-popup {
        .popup-bubble-anchor-tip {
          top: unset;
          transform: translateX(-50%);
          clipPath {
            .chrome & {
              transform: none;
            }
          }
        }
      }
    }

    // Hide circle marker where the thumb will be
    // Allow cursor to scroll the timeline while dragging for sorting.
    // Remove this css style, we can't scroll while dragging post in moble
    body > .wish-wrapper {
      .wish-popup-marker {
        display: none;
      }
    }

    .line-wrapper:first-of-type {
      min-width: $distance-between-posts-small-screen - 90px;
      .line {
        $line-dash-length: $distance-between-posts-small-screen/$number-of-dashes-between-circles;
        min-width: $distance-between-posts-small-screen - 90px;
        background-position: $line-dash-length 0px;
      }
    }
    .line-group-wrapper .line-wrapper .line {
      background-image: var(--timeline-dash-background-image);
    }
  }
}

html.screenshot {
  body {
    overflow: auto;
  }
  #wall-container {
    overflow: visible;
  }
  .wish-list-content {
    flex-direction: column;
  }
  .real-wish,
  .timeline-wish {
    max-height: 1000px !important;
    max-height: none !important;
  }
  .wish-draggable-wrapper {
    flex-grow: 1;
    height: unset;
    position: static;
    transform: translateX(-54px);
    .wish-wrapper {
      display: flex;
      height: unset;
    }
    .wish-wrapper.above-line {
      flex-direction: column-reverse;
      width: 180px;
      .circle-marker {
        position: static;
        transform: translate(-2px, 29px);
      }
      .wish-popup.above-line {
        transform: translate(-2px, calc(-100% - 10px)) !important;
        align-self: flex-end;
        position: static;
      }
    }
    .wish-wrapper.below-line {
      transform: translate(-2px, 28px);
      .wish-popup {
        margin-bottom: 16px;
      }
    }
  }
  .line-group-wrapper {
    flex-grow: 0;
    flex-shrink: 1;
    transform: translateY(28px);
  }
  .wish-list-wrapper#wish-list {
    overflow: visible !important;
    flex-shrink: 0;
    flex-grow: 1;
  }
  .scroll-button {
    display: none;
  }
}
</style>
